/**
 * useSpeechRecognition - Robust speech recognition hook
 * 
 * Features:
 * - Persistent listening (auto-restart)
 * - Noise filtering
 * - Confidence checking
 * - Error recovery
 *
 * Confidence (audit finding B44): the recogniser's own confidence in the
 * result, plus the alternatives maxAlternatives already asks for, are emitted
 * rather than discarded. The engine consumes them - see
 * client/src/engine/scoring.js assessRecognition(), which uses confidence to
 * mark an attempt inconclusive instead of scoring a bad microphone as a
 * failure.
 *
 * Not every browser populates confidence. Firefox and some Safari builds
 * report `undefined` or exactly 0 on final results; both are passed through
 * unchanged as null, and the engine treats null as "unknown, trust the
 * result" rather than "zero confidence".
 *
 * Speech onset: `onSpeechStart(cb)` registers a callback fired from the
 * recogniser's own `onspeechstart` event, stamped with performance.now().
 * client/src/engine/trial.js requires this timestamp and says so explicitly -
 * "onspeechstart, NOT the first interim result", because interim results
 * arrive hundreds of milliseconds late and only once the recogniser has
 * decided it heard a WORD, which is exactly what Phase 1 component B must not
 * depend on. Without it a game cannot compute onset latency and Phase 2 A/B/C
 * are unmeasurable. Purely additive: existing callers are unaffected.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { redact } from '../engine/language.js';

/**
 * Normalise a SpeechRecognitionAlternative confidence.
 *
 * A browser that does not score confidence reports `undefined` or exactly 0.
 * Both become null - "unknown", never "certainly wrong". scoring.js
 * assessRecognition() trusts a null and applies no gate; without this
 * distinction the gate would discard every attempt on Firefox and on the
 * Safari builds that behave the same way.
 *
 * @param {number|null|undefined} raw
 * @returns {number|null}
 */
export function normaliseConfidence(raw) {
  return (raw === undefined || raw === null || raw === 0) ? null : raw;
}

/**
 * Read one SpeechRecognition `onresult` event into the shape the engine wants.
 *
 * PURE, AND EXPORTED FOR A REASON. This is the seam audit finding B44 was
 * about: `maxAlternatives = 3` was set and only `result[0].transcript` was
 * read, so the single most load-bearing input to the colour system was fetched
 * from the browser and thrown away. B44 is closed, but nothing pinned it -
 * every confidence test in the suite drives `test/support/scriptedStage.js`,
 * which FABRICATES the event this function is supposed to produce. So the hook
 * could quietly regress to dropping confidence again and the whole suite would
 * stay green, and the only symptom would be a chart that had silently gone
 * back to scoring bad microphones as failing children.
 *
 * Extracting it makes the seam testable without React, a DOM or a microphone.
 * See client/test/recognitionEvent.test.js.
 *
 * @param {{resultIndex:number, results:ArrayLike<object>}} event
 * @returns {{transcript:string, confidence:number|null,
 *            alternatives:Array<{transcript:string, confidence:number|null}>,
 *            interim:string}}
 *          `transcript` is '' when the event carried no final result.
 */
export function readRecognitionEvent(event) {
  let transcript = '';
  let confidence = null;
  let alternatives = [];
  let interim = '';

  const results = (event && event.results) || [];
  const from = (event && Number.isInteger(event.resultIndex)) ? event.resultIndex : 0;

  for (let i = from; i < results.length; i++) {
    const result = results[i];
    if (!result || !result[0]) continue;
    const text = String(result[0].transcript || '').toLowerCase().trim();

    if (result.isFinal) {
      transcript = text;
      confidence = normaliseConfidence(result[0].confidence);

      // maxAlternatives is 3; read them all instead of only result[0]. They
      // are the evidence that shows a child said "fox" and was marked wrong
      // because the recogniser returned something else - see CLAUDE.md section 5.
      alternatives = [];
      for (let a = 0; a < result.length; a++) {
        if (!result[a]) continue;
        alternatives.push({
          transcript: String(result[a].transcript || '').toLowerCase().trim(),
          confidence: normaliseConfidence(result[a].confidence),
        });
      }
    } else {
      interim = text;
    }
  }

  return { transcript, confidence, alternatives, interim };
}

export function useSpeechRecognition() {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [confidence, setConfidence] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const onResultCallbackRef = useRef(null);
  const onSpeechStartCallbackRef = useRef(null);
  const isManualStopRef = useRef(false);
  const restartTimeoutRef = useRef(null);

  // Initialize
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      setIsListening(false);
      
      // Auto-restart unless manually stopped
      if (!isManualStopRef.current) {
        restartTimeoutRef.current = setTimeout(() => {
          try {
            recognition.start();
          } catch (e) {
            // Ignore - might already be started
          }
        }, 100);
      }
    };

    // Fires when the recogniser first detects speech-like audio, before any
    // transcript exists. The only clean onset signal the Web Speech API offers.
    recognition.onspeechstart = () => {
      if (onSpeechStartCallbackRef.current) {
        onSpeechStartCallbackRef.current(performance.now());
      }
    };

    recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error);
      
      switch (event.error) {
        case 'no-speech':
          // Normal - just silence, don't show error
          break;
        case 'audio-capture':
          setError('No microphone found');
          break;
        case 'not-allowed':
          setError('Microphone permission denied');
          isManualStopRef.current = true; // Don't auto-restart
          break;
        case 'network':
          // Try to recover
          break;
        default:
          // Don't show error for minor issues
          break;
      }
    };

    recognition.onresult = (event) => {
      const {
        transcript: finalTranscript,
        confidence: finalConfidence,
        alternatives: finalAlternatives,
        interim,
      } = readRecognitionEvent(event);

      // The interim transcript is DISPLAY ONLY - engine/trial.js is explicit
      // that an interim result is never a substitute for the onset, and nothing
      // judges from it. So it is the one transcript that can be screened at
      // source, which covers every game at once including the ones not built on
      // the engine. A real session drew an obscenity on the play screen this
      // way, live, in front of the child. See engine/language.js.
      setInterimTranscript(redact(interim));

      if (finalTranscript) {
        // Filter out very short noise
        if (finalTranscript.length >= 2) {
          setTranscript(finalTranscript);
          setInterimTranscript('');
          setConfidence(finalConfidence);
          setAlternatives(finalAlternatives);

          if (onResultCallbackRef.current) {
            // Second argument is additive: existing callers taking only the
            // transcript string are unaffected.
            onResultCallbackRef.current(finalTranscript, {
              confidence: finalConfidence,
              alternatives: finalAlternatives,
            });
          }
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      isManualStopRef.current = true;
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      try {
        recognition.stop();
      } catch (e) {}
    };
  }, []);

  const startListening = useCallback(() => {
    if (!recognitionRef.current || !isSupported) return false;

    isManualStopRef.current = false;
    setTranscript('');
    setInterimTranscript('');
    setConfidence(null);
    setAlternatives([]);
    setError(null);

    try {
      recognitionRef.current.start();
      return true;
    } catch (e) {
      if (e.name === 'InvalidStateError') {
        // Already started
        return true;
      }
      console.warn('Failed to start recognition:', e);
      return false;
    }
  }, [isSupported]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    isManualStopRef.current = true;
    
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }

    try {
      recognitionRef.current.stop();
    } catch (e) {}

    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const onResult = useCallback((callback) => {
    onResultCallbackRef.current = callback;
  }, []);

  // Register a speech-onset listener. The callback receives a performance.now()
  // timestamp - the same clock engine/trial.js requires for every field of a
  // trial record. Not every browser fires onspeechstart; a caller that needs an
  // onset on those should fall back to audio-energy detection.
  const onSpeechStart = useCallback((callback) => {
    onSpeechStartCallbackRef.current = callback;
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setConfidence(null);
    setAlternatives([]);
  }, []);

  return {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    confidence,
    alternatives,
    error,
    startListening,
    stopListening,
    onResult,
    onSpeechStart,
    clearTranscript,
  };
}

export default useSpeechRecognition;
